// Chart drawing tools — adapted from OpenCharts' drawing engine
// (https://github.com/dylanpersonguy/OpenCharts, MIT, see LICENSE-OpenCharts),
// ported to lightweight-charts v5 and trimmed to what the desk uses.

/**
 * Armable tools. Some only change how a drawing is placed: "ray" / "extended"
 * store a trendline with extend flags, "long-position" / "short-position"
 * store a "position" with its side, and "measure" never stores anything.
 */
export type DrawingTool =
  | "none"
  | "trendline"
  | "ray"
  | "extended"
  | "horizontal"
  | "vertical"
  | "channel"
  | "fibonacci"
  | "fibextension"
  | "rectangle"
  | "ellipse"
  | "triangle"
  | "arrow"
  | "long-position"
  | "short-position"
  | "measure"
  | "text";

export const DRAWING_TYPES = [
  "trendline",
  "horizontal",
  "vertical",
  "channel",
  "fibonacci",
  "fibextension",
  "rectangle",
  "ellipse",
  "triangle",
  "arrow",
  "position",
  "text",
] as const;

/** What a stored drawing is. */
export type DrawingType = (typeof DRAWING_TYPES)[number];

export type DrawingLineStyle = "solid" | "dashed" | "dotted";

/**
 * One drawing, in chart data space: unix-second times and prices, so it lands
 * on the same candles on every timeframe. Plain JSON, saved per coin.
 */
export interface Drawing {
  id: string;
  type: DrawingType;
  /** First anchor. A horizontal line only has a price. */
  price: number;
  time?: number;
  price2?: number;
  time2?: number;
  /** Third anchor: the second line of a parallel channel. */
  price3?: number;
  time3?: number;
  color: string;
  /** Line width in px (default 2). */
  width?: number;
  lineStyle?: DrawingLineStyle;
  /** Locked drawings can be selected but not moved. */
  locked?: boolean;
  /** Trendline only: run the line on to the pane edge. */
  extendLeft?: boolean;
  extendRight?: boolean;
  /** Text drawings. */
  text?: string;
  /** Position tool: entry = price, plus a stop and a target. */
  side?: "long" | "short";
  stopPrice?: number;
  targetPrice?: number;
}

/** One gesture's worth of edits: a single undo step. */
export interface DrawingChange {
  added?: Drawing[];
  updated?: Drawing[];
  removed?: string[];
}

/** A point in chart data space (unix-second time + price). */
export interface DataPoint {
  time: number;
  price: number;
}

export type EntryState = "normal" | "hovered" | "selected" | "preview";

export interface ResolvedFibLevel {
  level: number;
  price: number;
  y: number | null;
  label: string;
}

/** A drawing with its anchors resolved to pane pixels (null = off the chart). */
export interface ResolvedEntry {
  d: Drawing;
  x1: number | null;
  y1: number | null;
  x2: number | null;
  y2: number | null;
  /** Trendlines: the segment actually drawn, stretched when extended. */
  seg?: { x1: number; y1: number; x2: number; y2: number };
  fibLevels?: ResolvedFibLevel[];
  /** Trendline change / % / bars readout, while selected or being drawn. */
  stats?: string[];
  yStop?: number | null;
  yTarget?: number | null;
  x3?: number | null;
  y3?: number | null;
  state: EntryState;
}

/** Canvas colours (rgba / hex strings) the drawings paint with. */
export interface DrawTheme {
  /** Readout boxes and handles. */
  surface: string;
  text: string;
  muted: string;
  /** Position tool: target zone / stop zone. */
  profit: string;
  loss: string;
  font: string;
}

/** What the renderer needs besides the drawings. */
/** A new Buy / Sell plan's target: twice the risk (the system's 1:2 plan). */
export const PLAN_REWARD = 2;

export interface DrawInfo {
  theme: DrawTheme;
  /** Dollars risked per trade (day-risk settings); 0 hides the size readout. */
  riskUsd: number;
  /** Width (px) of the pane's right edge kept clear for price-line titles. */
  rightLane: number;
  priceFormat: (price: number) => string;
}

export type TimeKey = "time" | "time2" | "time3";
export type PriceKey = "price" | "price2" | "price3" | "stopPrice" | "targetPrice";

/** Where a hit landed: a draggable anchor (maybe one axis only) or the body. */
export type HitRegion =
  | { kind: "point"; timeKey: TimeKey | null; priceKey: PriceKey | null }
  | { kind: "body" };

export interface Hit {
  id: string;
  region: HitRegion;
}

export interface DrawingCallbacks {
  /** A gesture finished: one undo step. */
  onChange: (change: DrawingChange) => void;
  /** The armed tool finished (drawing placed, or Escape). */
  onToolFinished: () => void;
  onSelectionChange: (ids: string[]) => void;
  /** Double-click / long-press on a drawing (e.g. edit its text). */
  onRequestEdit?: (id: string) => void;
  /** Alt+T/H/F/R/M pressed: arm that tool. */
  onSelectTool?: (tool: DrawingTool) => void;
  onUndo?: () => void;
  onRedo?: () => void;
}

/**
 * Swatches for the selected drawing. The first is the default: pink, the one
 * hue no chart line uses (EMAs are sky / violet, VWAP gold, prior day orange,
 * week open white) — a blue default vanished into the EMA 20.
 */
export const DRAWING_COLORS = [
  "#FF2E88", // pink
  "#3B82F6", // blue
  "#22D3EE", // cyan
  "#D1FE17", // lime
  "#FF8A1F", // orange
  "#A855F7", // violet
  "#94A3B8", // slate
  "#F5F5F6", // white
] as const;

export const DRAWING_WIDTHS = [1, 2, 3, 4] as const;

export const DEFAULT_DRAWING_COLOR: string = DRAWING_COLORS[0];
